import { ReplaySubject } from 'rxjs/ReplaySubject';
import { Observable } from 'rxjs/Observable';
import { cartService } from './CartService';
import { IPixelModel } from 'interfaces/IPixelModel';
import { apiService } from 'shared/services';
import { PIXEL_URL } from 'settings';
import { paymentService } from './PaymentService';
import { of } from 'rxjs/observable/of';
import { parameterService } from 'services/ParameterService';
import { enParameters } from 'enums/enParameters';
import { Cookies } from 'react-cookie';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';

export enum enPixelPath {
  CHECKOUT = 'checkout',
  CREDITCARD = 'creditcard',
  BANKSLIP = 'bankslip',
  BANKSLIP_VIEW = 'bankslip-view'
}

export interface IPixel {
  facebook: any;
  analytics: any;
  adwords: any;
  taboola: any;
  outbrain: any;
  tagmanager: any;
}

export class PixelService {
  private pixelPath$ = new ReplaySubject<enPixelPath>(1);
  private pixelModel$ = new ReplaySubject<IPixelModel>(1);
  private hasTagmanager$ = new BehaviorSubject<boolean>(true);

  constructor() {
    cartService
      .getCart()
      .take(1)
      .map(cart => {
        let model: IPixelModel = {
          contentId: cart.contentId,
          producerId: cart.config.producerId,
          affiliateId: cart.config.affiliateId
        };

        return model;
      })
      .subscribe((model: IPixelModel) => {
        this.pixelModel$.next(model);
      });

    Observable.combineLatest(cartService.getCart(), paymentService.getCart(), paymentService.getPaymentResult())
      .map(([cart, payment, paymentResult]) => {
        let model: IPixelModel = {
          contentId: cart.contentId,
          producerId: cart.config.producerId,
          affiliateId: cart.config.affiliateId || null
        };

        model.user = {
          name: payment.name || '',
          email: payment.email || ''
        };

        model.payment = {
          product: cart.content.title,
          price: paymentResult.paidAmount,
          quantity: cart.products[0].quantity || 1,
          currency: cart.config.currency,
          paymentType: cart.config.paymentType,
          normalizedPaymentType: paymentResult.paymentMethod,
          transactionId: cart.transactionKey,

          thankyouUrl: paymentResult.thankyouUrl || null,
          campaign: cart.config.pixel.campaign || null,
          subscription: cart.subscription || null
        };

        return model;
      })
      .subscribe((model: IPixelModel) => {
        this.pixelModel$.next(model);
      });
  }

  setPath(path: enPixelPath): void {
    this.pixelPath$.next(path);
  }

  getPath(): Observable<enPixelPath> {
    return this.pixelPath$.asObservable();
  }

  init(): Observable<IPixel> {
    return this.pixelPath$
      .asObservable()
      .switchMap(path => {
        return Observable.combineLatest(of(path), this.pixelModel$.asObservable()).take(1);
      })
      .switchMap(([path, model]) => {
        const hash = Buffer.from(JSON.stringify(model)).toString('base64');
        return apiService.get<IPixel>(`${PIXEL_URL}/pixel/${path}/${hash}`);
      })
      .do((pixel) => {
        if (pixel.tagmanager) {
          this.unsetTagmanager();
        }
      });
  }

  setAffiliationCookie() {
    return parameterService
      .get(enParameters.a)
      .filter(param => !!param)
      .switchMap(() => this.pixelModel$)
      .filter(pixel => !!pixel.contentId)
      .map(pixel => {
        return `https://my.eduzz.com/lp/edz/${pixel.contentId}/${pixel.producerId}`;
      });
  }

  setPixel(model: IPixelModel) {
    this.pixelModel$.next(model);
  }

  hasCookie(contentId: string): boolean {
    const cookie = new Cookies();

    if (cookie.get(`pixel-${contentId}`)) {
      cookie.remove(`pixel-${contentId}`, { path: '/' });

      return true;
    }

    return false;
  }

  unsetTagmanager() {
    this.hasTagmanager$.next(false);
  }

  hasTagmanager() {
    return this.hasTagmanager$.asObservable();
  }
}

// tslint:disable-next-line:variable-name
export const PixelServiceFactory = () => new PixelService();

export const pixelService = PixelServiceFactory();
